/**
 * PeeringDB Integration Module
 * Fetches and displays peering information from PeeringDB API
 */

import type { PeeringDBResponse, NetworkInfo } from '../types/peeringdb';

const ASN = 210577;
const PEERINGDB_API_URL = `https://www.peeringdb.com/api/net?asn=${ASN}`;
const PEERINGDB_PROFILE_URL = `https://www.peeringdb.com/asn/${ASN}`;
const API_TIMEOUT = 5000; // 5 seconds

/**
 * Initialize PeeringDB integration
 */
export async function initializePeeringDB(): Promise<void> {
  const container = document.getElementById('peeringdbInfo');

  if (!container) {
    console.error('PeeringDB container not found');
    return;
  }

  try {
    // Fetch data from PeeringDB API with timeout
    const data = await fetchWithTimeout(PEERINGDB_API_URL, API_TIMEOUT);

    if (data && data.data && data.data.length > 0) {
      // Successfully retrieved data
      const networkInfo = data.data[0];
      displayPeeringDBInfo(container, networkInfo);
      console.log('PeeringDB data loaded successfully:', networkInfo);
    } else {
      // No data found, show fallback
      displayFallback(container);
    }
  } catch (error) {
    console.error('Error fetching PeeringDB data:', error);
    displayFallback(container);
  }
}

/**
 * Fetch with timeout
 */
async function fetchWithTimeout(url: string, timeout: number): Promise<PeeringDBResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json'
      }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json() as PeeringDBResponse;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

/**
 * Display PeeringDB information
 */
function displayPeeringDBInfo(container: HTMLElement, networkInfo: NetworkInfo): void {
  const policyMap: Record<string, string> = {
    'Open': 'Open',
    'Selective': 'Selective',
    'Restrictive': 'Restrictive',
    'No': 'No Peering'
  };

  const ratioMap: Record<string, string> = {
    'Heavy Outbound': 'Heavy Outbound',
    'Mostly Outbound': 'Mostly Outbound',
    'Balanced': 'Balanced',
    'Mostly Inbound': 'Mostly Inbound',
    'Heavy Inbound': 'Heavy Inbound'
  };

  const policy = networkInfo.policy_general
    ? (policyMap[networkInfo.policy_general] || networkInfo.policy_general)
    : 'Not specified';
  const ratio = networkInfo.info_ratio
    ? (ratioMap[networkInfo.info_ratio] || networkInfo.info_ratio)
    : 'Not specified';
  const name = networkInfo.name || `AS${ASN}`;

  let html = `
    <div class="peeringdb-info">
      <div style="margin-bottom: 1rem;">
        <span class="peeringdb-badge">✓ Verified on PeeringDB</span>
      </div>

      <div class="peeringdb-detail">
        <strong>Network Name:</strong> ${escapeHtml(name)}
      </div>

      <div class="peeringdb-detail">
        <strong>ASN:</strong> ${ASN}
      </div>

      <div class="peeringdb-detail">
        <strong>Peering Policy:</strong> ${escapeHtml(policy)}
      </div>

      <div class="peeringdb-detail">
        <strong>Traffic Ratio:</strong> ${escapeHtml(ratio)}
      </div>
  `;

  if (networkInfo.notes && networkInfo.notes.trim()) {
    html += `
      <div class="peeringdb-detail" style="margin-top: 1rem;">
        <strong>Notes:</strong>
        <p style="margin-top: 0.5rem; padding: 0.75rem; background: #f8fafc; border-radius: 0.375rem; font-size: 0.875rem;">
          ${escapeHtml(networkInfo.notes)}
        </p>
      </div>
    `;
  }

  html += `
      <div style="margin-top: 1.5rem;">
        <a href="${PEERINGDB_PROFILE_URL}" target="_blank" rel="noopener"
           style="display: inline-block; background: #3b82f6; color: white; padding: 0.5rem 1rem;
                  border-radius: 0.375rem; text-decoration: none; font-weight: 600; transition: background 0.2s;">
          View Full PeeringDB Profile →
        </a>
      </div>
    </div>
  `;

  container.innerHTML = html;
}

/**
 * Display fallback content when PeeringDB data is unavailable
 */
function displayFallback(container: HTMLElement): void {
  const html = `
    <div class="peeringdb-info">
      <div class="peeringdb-detail">
        <strong>ASN:</strong> ${ASN}
      </div>

      <div class="peeringdb-detail">
        <strong>Peering Policy:</strong> Open
      </div>

      <div class="peeringdb-detail">
        <strong>Internet Exchanges:</strong>
        <ul style="margin: 0.5rem 0 0 1.25rem;">
          <li><a href="https://kleyrex.net" target="_blank" rel="noopener">KleyRex</a></li>
          <li><a href="https://locix.online" target="_blank" rel="noopener">LocIX</a></li>
          <li><a href="https://www.evix.org" target="_blank" rel="noopener">EVIX</a></li>
        </ul>
      </div>

      <div style="margin-top: 1.5rem; padding: 1rem; background: #f8fafc; border-radius: 0.375rem;
                  border-left: 4px solid #3b82f6;">
        <p style="margin: 0; font-size: 0.875rem; color: #64748b;">
          For more detailed peering information, please visit our
          <a href="${PEERINGDB_PROFILE_URL}" target="_blank" rel="noopener">PeeringDB profile</a>.
        </p>
      </div>
    </div>
  `;

  container.innerHTML = html;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
